import { Link } from "react-router-dom";
import { ArrowRight, QrCode, Sparkles, ShoppingBag, ScanLine, Layers, Heart } from "lucide-react";

const features = [
    { icon: ScanLine, title: "Scan to browse", desc: "Customers scan one QR and step into your full catalog. Zero apps to install, zero learning curve." },
    { icon: Sparkles, title: "AI on-model photos", desc: "Generate editorial model imagery with one click. No shoots. No studios. Your shelves become a magazine." },
    { icon: Layers, title: "Smart filters", desc: "Price, occasion, fabric, color, brand — buyers find what they love in seconds, not scrolls." },
    { icon: Heart, title: "Save & share", desc: "Customers save favorites and share to WhatsApp for the long-distance family approval." },
    { icon: ShoppingBag, title: "Trial bookings", desc: "Walk-in trial requests land directly with your store staff, complete with the chosen pieces." },
    { icon: QrCode, title: "Multi-shop ready", desc: "Each branch gets its own QR. Super admin manages access across every store, on one screen." },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-brand-beige text-brand-ink">
            {/* Top nav */}
            <header className="ayraa-glass sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
                        <span className="font-display text-2xl tracking-editorial">Vastra<span className="text-brand-bronze">.</span>AI</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/shop/silk-palace" className="ayraa-btn-ghost text-sm" data-testid="demo-shop-link">View demo</Link>
                        <Link to="/login" className="ayraa-btn-primary text-sm" data-testid="login-link">Sign in</Link>
                    </div>
                </div>
            </header>

            {/* HERO — split screen, editorial */}
            <section className="relative">
                <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-0 min-h-[88vh]">
                    {/* Left — copy */}
                    <div className="lg:col-span-7 px-6 sm:px-12 lg:px-20 py-16 lg:py-28 flex flex-col justify-center animate-fade-up">
                        <div className="editorial-eyebrow mb-8">
                            <span className="inline-block w-8 h-px bg-brand-bronze align-middle mr-3" />
                            Vastra AI · Made in India
                        </div>
                        <h1 className="editorial-h1 text-balance">
                            The future of
                            <br />
                            <em className="not-italic text-brand-bronze">heritage,</em> draped.
                        </h1>
                        <p className="mt-8 max-w-xl text-brand-muted text-base sm:text-lg leading-relaxed text-pretty">
                            Vastra AI turns your boutique into a living catalog. Customers scan,
                            browse, virtually drape themselves in every saree, and request trials —
                            while your staff stays free to close the sale that matters.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link to="/login" className="ayraa-btn-primary" data-testid="cta-start-store">
                                Open your boutique <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                            </Link>
                            <Link to="/shop/silk-palace" className="ayraa-btn-outline" data-testid="cta-customer-demo">
                                See a live shop
                            </Link>
                        </div>
                        <div className="mt-14 flex items-center gap-8 text-xs font-mono uppercase tracking-wider-2 text-brand-muted">
                            <div className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand-bronze" /> No app install</div>
                            <div className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand-bronze" /> Multi-shop</div>
                            <div className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand-bronze" /> WhatsApp</div>
                        </div>
                    </div>

                    {/* Right — full-bleed image */}
                    <div className="lg:col-span-5 relative bg-brand-ink overflow-hidden min-h-[60vh] lg:min-h-full">
                        <img
                            src="https://images.pexels.com/photos/28943579/pexels-photo-28943579.jpeg"
                            alt="Saree editorial"
                            className="absolute inset-0 w-full h-full object-cover animate-image-reveal"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/65 via-brand-ink/10 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-8 lg:p-12">
                            <div className="editorial-eyebrow text-brand-bronzelight">Bridal · Kanjivaram</div>
                            <div className="font-display text-3xl text-white mt-2">Maroon Heirloom Silk</div>
                            <div className="flex items-baseline gap-3 mt-2">
                                <span className="font-mono text-white text-base">₹24,500</span>
                                <span className="font-mono text-sm text-white/55 line-through">₹28,000</span>
                            </div>
                        </div>
                        {/* Hovering QR badge */}
                        <div className="hidden lg:flex absolute top-8 right-8 items-center gap-2 px-4 py-2 glass-dock-dark rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-soft-pulse" />
                            <span className="font-mono text-xs uppercase tracking-wider-2">QR live · silk-palace</span>
                        </div>
                    </div>
                </div>
                {/* hairline at the bottom */}
                <div className="h-px bg-brand-border" />
            </section>

            {/* WHAT YOU GET */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
                <div className="text-center">
                    <div className="editorial-eyebrow inline-flex items-center gap-3">
                        <span className="w-8 h-px bg-brand-bronze" /> What you get <span className="w-8 h-px bg-brand-bronze" />
                    </div>
                    <h2 className="editorial-h2 mt-6 text-balance">
                        A boutique-grade catalog, <em className="not-italic text-brand-bronze">in a weekend.</em>
                    </h2>
                    <p className="mt-5 text-brand-muted max-w-2xl mx-auto leading-relaxed text-pretty">
                        Built for the way Indian saree stores actually work — not another generic e-commerce template.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border mt-16 border border-brand-border rounded-sm overflow-hidden">
                    {features.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <div key={i} className="bg-brand-paper p-8 lg:p-10 group transition-colors duration-500 hover:bg-brand-subtle">
                                <div className="w-10 h-10 grid place-items-center border border-brand-ink/15 text-brand-ink mb-7 group-hover:bg-brand-ink group-hover:text-white group-hover:border-brand-ink transition-all duration-500">
                                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                                </div>
                                <div className="font-display text-2xl tracking-editorial">{f.title}</div>
                                <div className="text-brand-muted mt-3 leading-relaxed text-sm">{f.desc}</div>
                                <div className="mt-6 editorial-eyebrow opacity-50">0{i + 1} / 06</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* DARK SECTION — call to action */}
            <section className="bg-brand-ink text-brand-paper py-24 sm:py-32 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.12] bg-no-repeat bg-cover"
                    style={{ backgroundImage: "url(https://images.pexels.com/photos/33276443/pexels-photo-33276443.jpeg)" }} />
                <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/40 via-brand-ink/80 to-brand-ink" />
                <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center relative">
                    <div className="editorial-eyebrow text-brand-bronzelight">Open for boutiques</div>
                    <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl mt-6 tracking-editorial leading-[0.95] text-balance">
                        Stop opening one hundred sarees.
                        <br />
                        <em className="not-italic text-brand-bronze">Show ten thousand.</em>
                    </h2>
                    <p className="mt-8 text-white/65 max-w-2xl mx-auto leading-relaxed text-pretty">
                        Set up your shop, upload your inventory, and print one QR for every rack.
                        Your customers will thank you. Your staff will thank you more.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-3 justify-center">
                        <Link to="/login" className="ayraa-btn-bronze" data-testid="cta-footer-login">
                            Login to dashboard <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                        </Link>
                        <Link to="/shop/silk-palace" className="inline-flex items-center justify-center bg-transparent text-white border border-white/25 hover:border-brand-bronze hover:bg-white/5 transition-all duration-300 px-7 py-3 text-sm font-medium tracking-wide rounded-sm" data-testid="cta-footer-demo">
                            Try a demo shop
                        </Link>
                    </div>
                </div>
            </section>

            {/* footer */}
            <footer className="bg-brand-beige border-t border-brand-border">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="font-display text-xl tracking-editorial">Vastra<span className="text-brand-bronze">.</span>AI</div>
                    <div className="font-mono text-[11px] uppercase tracking-wider-2 text-brand-muted">
                        Crafted in India · For India&apos;s heritage boutiques
                    </div>
                </div>
            </footer>
        </div>
    );
}
